import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import ECreateDetailsCopyItem from '@/components/everridge/ECreateDetailsCopyItem';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ETextInput from '@/components/everridge/ETextInput';
import EMarkdownEditor from '@/components/everridge/EMarkdownEditor';
import { ReleaseManageFormData, ReleaseManageSchema } from '../validators/ReleaseManageValidator';
import { useReleaseManageApi } from '../hooks/useReleaseManageApi';
import { CreateAppReleaseRequestDto, UpdateAppReleaseRequestDto } from '../types/releases-dto';
import EToogler from '@/components/everridge/EToogler';
import EDatePicker from '@/components/everridge/EDatePicker';
import EDropDownMenu from '@/components/everridge/EDropDownMenu';
import MoreVerticalIcon from '@/components/icons/MoreVerticalIcon';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import EInfoModal from '@/components/everridge/EInfoModal';

type Props = {
    id?: string;
    onClose: (state: boolean) => void;
};

const ReleaseManage: FC<Props> = ({ id, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();

    const { releaseDetailsData, createRelease, updateRelease, deleteRelease, sendRelease } =
        useReleaseManageApi({ id });

    const { control, handleSubmit, reset } = useForm<ReleaseManageFormData>({
        resolver: zodResolver(ReleaseManageSchema),
        defaultValues: {
            isActive: false,
        },
    });

    useEffect(() => {
        if (releaseDetailsData) {
            reset({
                version: releaseDetailsData.version,
                releasedAt: new Date(releaseDetailsData.releasedAt),
                content: releaseDetailsData.content,
                isActive: releaseDetailsData.isActive,
            });
        }
    }, [releaseDetailsData, reset]);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleCreateForm = async (form: ReleaseManageFormData) => {
        const dto: CreateAppReleaseRequestDto = {
            version: form.version,
            releasedAt: form.releasedAt,
            content: form.content,
            isActive: form.isActive,
        };

        return await createRelease(dto);
    };

    const handleUpdateForm = async (form: ReleaseManageFormData) => {
        const dto: UpdateAppReleaseRequestDto = {
            version: form.version,
            releasedAt: form.releasedAt,
            content: form.content,
            isActive: form.isActive,
        };

        return await updateRelease(dto);
    };

    const handleDeleteForm = async () => {
        await deleteRelease();
        handleClose();
    };

    const handleSubmitForm = () => {
        const submitHandler = id
            ? handleSubmit((form) => handleUpdateForm(form))
            : handleSubmit((form) => handleCreateForm(form));

        submitHandler();
        handleClose();
    };

    const handleSendRelease = () => {
        const submitHandler = id
            ? handleSubmit((form) => handleUpdateForm(form).then(() => sendRelease(id)))
            : handleSubmit((form) =>
                  handleCreateForm(form).then((res) => sendRelease(res.id)),
              );

        submitHandler();
        handleClose();
    };

    const createButtonsBlock = (
        <div className="w-full flex gap-4">
            <Button className="w-1/2" onClick={handleSendRelease}>
                {t('elements.buttons.send')}
            </Button>
            <Button className="w-1/2" onClick={handleSubmitForm}>
                {t('elements.buttons.create')}
            </Button>
        </div>
    );

    const updateButtonBlock = (
        <div className="w-full flex gap-4">
            <Button className="w-1/2" onClick={handleSendRelease}>
                {t('elements.buttons.send')}
            </Button>
            <Button className="w-1/2" onClick={handleSubmitForm}>
                {t('elements.buttons.update')}
            </Button>
        </div>
    );

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
                <SheetHeader>
                    <SheetDescription />
                    <SheetTitle>
                        {!isMobile && (
                            <SheetClose
                                className={cn(
                                    'flex items-center justify-center cursor-pointer',
                                    isMobile && 'size-11 border border-popover-100 rounded-full',
                                )}
                            >
                                <MoveLeftIcon />
                            </SheetClose>
                        )}
                        {id
                            ? t('main.releases.components.manage.title')
                            : t('main.releases.components.manage.title2')}
                        {isMobile && (
                            <SheetClose className="size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full">
                                <Cancel_01_Icon />
                            </SheetClose>
                        )}
                    </SheetTitle>
                </SheetHeader>
                <div className="flex-1 flex flex-col bg-popover-30 overflow-hidden select-none">
                    <div
                        className={cn(
                            'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
                            isMobile && 'p-4',
                        )}
                    >
                        {!!id && (
                            <div
                                className={
                                    'py-10 px-6 flex flex-col items-center gap-4 rounded-2.5xl bg-popover-10 border border-popover-100'
                                }
                            >
                                <div className="h-10 flex items-center justify-center gap-2 text-center">
                                    <span>ID:</span>
                                    <ECreateDetailsCopyItem
                                        type="ID"
                                        className={cn(isMobile && 'justify-between')}
                                    >
                                        {releaseDetailsData?.id}
                                    </ECreateDetailsCopyItem>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <h3 className={cn(isMobile && 'text-base')}>
                                {t('main.releases.components.manage.body.form')}
                            </h3>
                            <div
                                className={cn(
                                    'p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
                                    isMobile && 'p-0 border-0 bg-popover-30',
                                )}
                            >
                                <ETextInput
                                    control={control}
                                    fieldName="version"
                                    label={t('main.releases.components.manage.form.version.title')}
                                    placeholder={t(
                                        'main.releases.components.manage.form.version.placeholder',
                                    )}
                                />
                                <EMarkdownEditor
                                    control={control}
                                    fieldName="content"
                                    label={t('main.releases.components.manage.form.content.title')}
                                    placeholder={t(
                                        'main.releases.components.manage.form.content.placeholder',
                                    )}
                                />
                                <EDatePicker
                                    control={control}
                                    fieldName="releasedAt"
                                    label={t(
                                        'main.releases.components.manage.form.releasedAt.title',
                                    )}
                                    placeholder={t(
                                        'main.releases.components.manage.form.releasedAt.placeholder',
                                    )}
                                />
                                <EToogler
                                    control={control}
                                    fieldName="isActive"
                                    text={t('main.releases.components.manage.form.status.title')}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <h3 className={cn(isMobile && 'text-base')}>
                                {t('elements.buttons.otherOptions')}
                            </h3>
                            <EDropDownMenu
                                icon={MoreVerticalIcon}
                                buttonClassName="w-5 py-0! hover:bg-none!"
                                buttonVariant={'ghost'}
                            >
                                <Button
                                    variant="ghost"
                                    className="justify-start rounded-none!"
                                    onClick={() => setShowDeleteWarning(true)}
                                >
                                    <Delete_02_Icon className="stroke-error-1000" />
                                    <span className="truncate text-inherit leading-none">
                                        {t('elements.buttons.delete')}
                                    </span>
                                </Button>
                            </EDropDownMenu>
                        </div>
                    </div>
                </div>
                <SheetFooter>{id ? updateButtonBlock : createButtonsBlock}</SheetFooter>
                <Toaster />
            </SheetContent>
            {showDeleteWarning && (
                <EInfoModal
                    label={t('main.releases.components.manage.discard.title')}
                    description={t('main.releases.components.manage.discard.description')}
                    submitName={t('elements.buttons.delete')}
                    onSubmit={handleDeleteForm}
                    closeName={t('elements.buttons.cancel')}
                    onClose={() => setShowDeleteWarning(false)}
                />
            )}
        </Sheet>
    );
};

export default ReleaseManage;
